import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { sendToTelegram, FormData } from './telegramService';
import { AiImageBadge } from '../components/AiImageBadge';

// Varianti per le animazioni
const fadeIn = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { duration: 1 } }
};

const slideUp = {
  hidden: { opacity: 0, y: 30 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8 } }
};

const Contact: React.FC = () => {
  const [nome, setNome] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [telefono, setTelefono] = useState<string>("");
  const [messaggio, setMessaggio] = useState<string>("");
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isSuccess, setIsSuccess] = useState<boolean>(false);
  const [isError, setIsError] = useState<boolean>(false);

  // Funzione per gestire l'invio del form
  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);

    const formData: FormData = {
      nome,
      email,
      telefono,
      messaggio
    };

    try {
      // Utilizziamo il servizio Telegram per inviare i dati
      const success = await sendToTelegram(formData);

      if (success) {
        setIsSuccess(true);
        // Resettiamo i campi del form
        setNome("");
        setEmail("");
        setTelefono("");
        setMessaggio("");
      } else {
        setIsError(true);
      }
    } catch (error) {
      console.error('Errore durante l\'invio del form:', error);
      setIsError(true);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="relative h-[40vh]">
        <div className="absolute inset-0 bg-black bg-opacity-40 z-10"></div>
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url("/contatti-hero.jpg")' }}
        ></div>
        {/* TODO: rimuovi il badge sotto se contatti-hero.jpg è una foto reale, non generata con IA */}
        <AiImageBadge position="bottom-right" />

        <div className="container mx-auto px-4 h-full flex items-center relative z-20">
          <div className="max-w-3xl">
            <motion.h1
              className="font-playfair text-4xl md:text-5xl text-white mb-4"
              initial="hidden"
              animate="visible"
              variants={fadeIn}
            >
              Contatti
            </motion.h1>
            <motion.p
              className="font-montserrat text-lg md:text-xl text-white mb-8"
              initial="hidden"
              animate="visible"
              variants={slideUp}
            >
              Siamo a tua disposizione per qualsiasi informazione
            </motion.p>
          </div>
        </div>
      </section>

      {/* Informazioni di contatto e form */}
      <section className="py-20 bg-beige">
        <div className="container mx-auto px-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Informazioni di contatto */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <h2 className="font-playfair text-3xl text-verde-scuro mb-8">Come raggiungerci</h2>

              <div className="space-y-8">
                <div>
                  <h3 className="font-playfair text-xl text-verde-scuro mb-3">Indirizzo</h3>
                  <p className="font-montserrat text-verde-oliva flex items-start">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 mt-1 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    <span><a target='blank' href="https://www.google.com/maps/place//data=!4m2!3m1!1s0x133bc536b8d0c643:0x60022f19c7241127?sa=X&ved=1t:8290&ictx=111"> Via Madonna delle Grazie, 17, 84081 Baronissi SA</a></span>
                  </p>
                </div>

                <div>
                  <h3 className="font-playfair text-xl text-verde-scuro mb-3">Contatti</h3>
                  <p className="font-montserrat text-verde-oliva flex items-center mb-2">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                    </svg>
                    <span>Tel: 347.3721876</span>
                  </p>
                  <p className="font-montserrat text-verde-oliva flex items-center">
                    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5 mr-2 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                    </svg>
                    <span>Email: <a href="mailto:info@villadarcobaronissi.com">info@villadarcobaronissi.com</a></span>
                  </p>
                </div>

                <div>
                  <h3 className="font-playfair text-xl text-verde-scuro mb-3">Orari</h3>
                  <div className="font-montserrat text-verde-oliva">
                    <p className="font-semibold mb-1">Ristorante:</p>
                    {/*<p className="mb-2">Martedì - Domenica: 12:00 - 15:00, 19:00 - 23:00<br />Lunedì: Chiuso</p>*/}
                    <p>In allestimento</p>
                    <p className="font-semibold mb-1">Azienda Agricola:</p>
                    {/*<p>Lunedì - Sabato: 09:00 - 18:00<br />Domenica: Chiuso</p>*/}
                    <p>Ricezione solo su appuntamento</p>
                  </div>
                </div>

                <div>
                  <h3 className="font-playfair text-xl text-verde-scuro mb-3">Seguici sui social</h3>
                  <div className="flex space-x-4">
                    <a href="https://www.facebook.com/villadarcobaronissi/" target='_blank'  rel="noopener noreferrer" className="text-verde-oliva hover:text-oro transition-colors duration-300">
                      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path fillRule="evenodd" d="M22 12c0-5.523-4.477-10-10-10S2 6.477 2 12c0 4.991 3.657 9.128 8.438 9.878v-6.987h-2.54V12h2.54V9.797c0-2.506 1.492-3.89 3.777-3.89 1.094 0 2.238.195 2.238.195v2.46h-1.26c-1.243 0-1.63.771-1.63 1.562V12h2.773l-.443 2.89h-2.33v6.988C18.343 21.128 22 16.991 22 12z" clipRule="evenodd" />
                      </svg>
                    </a>
                    <a href="https://www.instagram.com/villadarco_baronissi/" target='_blank'  rel="noopener noreferrer" className="text-verde-oliva hover:text-oro transition-colors duration-300">
                      <svg className="h-6 w-6" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
                        <path fillRule="evenodd" d="M12.315 2c2.43 0 2.784.013 3.808.06 1.064.049 1.791.218 2.427.465a4.902 4.902 0 011.772 1.153 4.902 4.902 0 011.153 1.772c.247.636.416 1.363.465 2.427.048 1.067.06 1.407.06 4.123v.08c0 2.643-.012 2.987-.06 4.043-.049 1.064-.218 1.791-.465 2.427a4.902 4.902 0 01-1.153 1.772 4.902 4.902 0 01-1.772 1.153c-.636.247-1.363.416-2.427.465-1.067.048-1.407.06-4.123.06h-.08c-2.643 0-2.987-.012-4.043-.06-1.064-.049-1.791-.218-2.427-.465a4.902 4.902 0 01-1.772-1.153 4.902 4.902 0 01-1.153-1.772c-.247-.636-.416-1.363-.465-2.427-.047-1.024-.06-1.379-.06-3.808v-.63c0-2.43.013-2.784.06-3.808.049-1.064.218-1.791.465-2.427a4.902 4.902 0 011.153-1.772A4.902 4.902 0 015.45 2.525c.636-.247 1.363-.416 2.427-.465C8.901 2.013 9.256 2 11.685 2h.63zm-.081 1.802h-.468c-2.456 0-2.784.011-3.807.058-.975.045-1.504.207-1.857.344-.467.182-.8.398-1.15.748-.35.35-.566.683-.748 1.15-.137.353-.3.882-.344 1.857-.047 1.023-.058 1.351-.058 3.807v.468c0 2.456.011 2.784.058 3.807.045.975.207 1.504.344 1.857.182.466.399.8.748 1.15.35.35.683.566 1.15.748.353.137.882.3 1.857.344 1.054.048 1.37.058 4.041.058h.08c2.597 0 2.917-.01 3.96-.058.976-.045 1.505-.207 1.858-.344.466-.182.8-.398 1.15-.748.35-.35.566-.683.748-1.15.137-.353.3-.882.344-1.857.048-1.055.058-1.37.058-4.041v-.08c0-2.597-.01-2.917-.058-3.96-.045-.976-.207-1.505-.344-1.858a3.097 3.097 0 00-.748-1.15 3.098 3.098 0 00-1.15-.748c-.353-.137-.882-.3-1.857-.344-1.023-.047-1.351-.058-3.807-.058zM12 6.865a5.135 5.135 0 110 10.27 5.135 5.135 0 010-10.27zm0 1.802a3.333 3.333 0 100 6.666 3.333 3.333 0 000-6.666zm5.338-3.205a1.2 1.2 0 110 2.4 1.2 1.2 0 010-2.4z" clipRule="evenodd" />
                      </svg>
                    </a>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Form di contatto */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, delay: 0.2 }}
            >
              <h2 className="font-playfair text-3xl text-verde-scuro mb-8">Scrivici</h2>

              <form onSubmit={handleSubmit} className="space-y-6 max-w-2xl mx-auto">
                <div>
                  <label htmlFor="nome" className="block text-sm font-medium text-marrone mb-1">Nome *</label>
                  <input
                    type="text"
                    id="nome"
                    value={nome}
                    onChange={(e) => setNome(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-verde-oliva"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-marrone mb-1">Email *</label>
                  <input
                    type="email"
                    id="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-verde-oliva"
                  />
                </div>

                <div>
                  <label htmlFor="telefono" className="block text-sm font-medium text-marrone mb-1">Telefono</label>
                  <input
                    type="tel"
                    name="phone"
                    id="telefono"
                    value={telefono}
                    onChange={(e) => setTelefono(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-verde-oliva"
                  />
                </div>

                <div>
                  <label htmlFor="messaggio" className="block text-sm font-medium text-marrone mb-1">Messaggio *</label>
                  <textarea
                    id="messaggio"
                    value={messaggio}
                    onChange={(e) => setMessaggio(e.target.value)}
                    required
                    rows={5}
                    className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-verde-oliva"
                  ></textarea>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full bg-verde-oliva hover:bg-verde-scuro text-white font-medium py-3 px-6 rounded-md transition duration-300 ease-in-out transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-verde-scuro"
                >
                  {isSubmitting ? 'Invio in corso...' : 'Invia messaggio'}
                </button>

                {isSuccess && (
                  <div className="mt-4 p-3 bg-green-100 text-green-800 rounded">
                    Grazie per il tuo messaggio! Ti risponderemo al più presto.
                  </div>
                )}

                {isError && (
                  <div className="mt-4 p-3 bg-red-100 text-red-800 rounded">
                    Si è verificato un errore nell'invio del messaggio. Riprova più tardi o contattaci direttamente.
                  </div>
                )}
              </form>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Mappa */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <motion.h2
            className="font-playfair text-3xl text-verde-scuro mb-8 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Dove siamo
          </motion.h2>

          <motion.div
            className="rounded-lg overflow-hidden shadow-xl h-[400px] bg-gray-200"
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
          >
            <div className="w-full h-full flex items-center justify-center">
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3719.2927694192635!2d14.785916481026593!3d40.74743308842831!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x133bc536b8d0c643%3A0x60022f19c7241127!2sAzienda%20Agricola%20Villa%20d&#39;Arco!5e1!3m2!1sit!2sit!4v1747519735933!5m2!1sit!2sit"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
              ></iframe>
            </div>
          </motion.div>

          <div className="mt-8 text-center">
            <p className="font-montserrat text-verde-oliva">
              Siamo facilmente raggiungibili in auto dalla Strada Statale 88 dei Due Principati.
              <br />
              Parcheggio gratuito disponibile per i nostri clienti.
            </p>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-20 bg-crema">
        <div className="container mx-auto px-4">
          <motion.h2
            className="font-playfair text-3xl text-verde-scuro mb-12 text-center"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            Domande frequenti
          </motion.h2>

          <div className="max-w-3xl mx-auto space-y-6">
            <motion.div
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <h3 className="font-playfair text-xl text-verde-scuro mb-2">Quando aprirà il ristorante?</h3>
              <p className="font-montserrat text-verde-oliva">
                Stiamo lavorando all’allestimento e alla cucina; seguiranno aggiornamenti sui canali social e sul sito.
              </p>
            </motion.div>

            <motion.div
              className="bg-white p-6 rounded-lg shadow-md"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <h3 className="font-playfair text-xl text-verde-scuro mb-2">Come posso acquistare i vostri prodotti?</h3>
              <p className="font-montserrat text-verde-oliva">
               Presto sarà possibile acquistare i nostri Funghi Cardoncelli su prenotazione. 
               Prima di tutto, ti invitiamo a contattarci tramite i nostri canali social per concordare l’ordine. 
               Una volta confermato l’ordine e fissato il giorno di ritiro, potrai venire a ritirare i prodotti direttamente presso la nostra struttura.
              </p>
            </motion.div>

          </div>
        </div>
      </section>
    </div>
  );
};

export default Contact;